/**
 * Слой проводки (кабели между приборами). Конец кабеля — прибор на стене или
 * светильник на потолке из слоя электрики; кабель без обоих концов не создаётся.
 */
import { create } from "zustand";
import { uid, type Cable, type ElectricalGroup, type LightingConnection } from "@/shared/plan";
import { getCatalogItem, isLight, isSwitch } from "@/shared/catalog";
import { useFixturesStore } from "./fixturesStore";
import type { Pt } from "@/shared/lib/geometry/geom";

export interface WiringLayerState {
	items: Record<string, Cable>;
	connections: Record<string, LightingConnection>;
	groups: Record<string, ElectricalGroup>;
	visible: boolean;

	addCable: (
		from: string,
		to: string,
		points: Pt[],
		material?: string,
		groupId?: string,
	) => string | null;
	addLightingConnection: (
		from: string,
		to: string,
		style: LightingConnection["style"],
	) => string | null;
	updateCable: (id: string, patch: Partial<Omit<Cable, "id" | "from" | "to">>) => void;
	updateLightingConnection: (id: string, patch: Partial<Pick<LightingConnection, "key">>) => void;
	addGroup: (group: Omit<ElectricalGroup, "id">) => string;
	updateGroup: (id: string, patch: Partial<Omit<ElectricalGroup, "id">>) => void;
	removeGroup: (id: string) => void;

	setVisible: (visible: boolean) => void;
	toggleVisible: () => void;

	restoreCables: (cables: Record<string, Cable>) => void;
	restoreLightingConnections: (connections: Record<string, LightingConnection>) => void;
	restoreGroups: (groups: Record<string, ElectricalGroup>) => void;
}

export const useWiringStore = create<WiringLayerState>((set, get) => ({
	items: {},
	connections: {},
	groups: {},
	visible: true,

	addCable: (from, to, points, material, groupId) => {
		const fixturesState = useFixturesStore.getState();
		const exists = (id: string) => id in fixturesState.items || id in fixturesState.ceilingItems;
		if (!exists(from) || !exists(to) || from === to) return null;

		const id = uid("c");
		const cable: Cable = {
			id,
			from,
			to,
			points: points.map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) })),
			...(material ? { material } : {}),
			...(groupId && get().groups[groupId] ? { groupId } : {}),
		};
		set((s) => ({
			items: { ...s.items, [id]: cable },
			visible: true,
		}));
		return id;
	},

	addLightingConnection: (from, to, style) => {
		const fixturesState = useFixturesStore.getState();
		const fromItem = fixturesState.items[from] ?? fixturesState.ceilingItems[from];
		const toItem = fixturesState.items[to] ?? fixturesState.ceilingItems[to];
		if (!fromItem || !toItem) return null;
		const fromSwitch = isSwitch(getCatalogItem(fromItem.ref));
		const toSwitch = isSwitch(getCatalogItem(toItem.ref));
		if (
			!(
				(fromSwitch && isLight(getCatalogItem(toItem.ref))) ||
				(toSwitch && isLight(getCatalogItem(fromItem.ref)))
			)
		)
			return null;
		if (
			Object.values(get().connections).some(
				(connection) =>
					(connection.from === from && connection.to === to) ||
					(connection.from === to && connection.to === from),
			)
		)
			return null;

		const id = uid("lc");
		const switchId = fromSwitch ? from : to;
		fixturesState.updateFixture(switchId, { lightingControl: "connections" });
		set((s) => ({
			connections: { ...s.connections, [id]: { id, from, to, style } },
			visible: true,
		}));
		return id;
	},
	updateLightingConnection: (id, patch) =>
		set((state) => {
			const connection = state.connections[id];
			return connection
				? { connections: { ...state.connections, [id]: { ...connection, ...patch } } }
				: state;
		}),

	updateCable: (id, patch) =>
		set((s) => {
			const cable = s.items[id];
			if (!cable) return s;

			const fixturesState = useFixturesStore.getState();
			const exists = (endId: string) =>
				endId in fixturesState.items || endId in fixturesState.ceilingItems;
			const next = { ...cable, ...patch };
			if (!exists(next.from) || !exists(next.to)) return s;

			return { items: { ...s.items, [id]: next } };
		}),

	addGroup: (group) => {
		const id = uid("eg");
		set((state) => ({ groups: { ...state.groups, [id]: { ...group, id } } }));
		return id;
	},
	updateGroup: (id, patch) =>
		set((state) => {
			const group = state.groups[id];
			return group ? { groups: { ...state.groups, [id]: { ...group, ...patch } } } : state;
		}),
	removeGroup: (id) =>
		set((state) => {
			if (!state.groups[id]) return state;
			const groups = { ...state.groups };
			delete groups[id];
			const items = Object.fromEntries(
				Object.entries(state.items).map(([key, cable]) =>
					cable.groupId === id ? [key, { ...cable, groupId: undefined }] : [key, cable],
				),
			);
			return { groups, items };
		}),

	setVisible: (visible) => set({ visible }),
	toggleVisible: () => set((s) => ({ visible: !s.visible })),

	restoreCables: (cables) => set({ items: cables }),
	restoreLightingConnections: (connections) => set({ connections }),
	restoreGroups: (groups) => set({ groups }),
}));
